import { useEffect, useState } from "react";
import { Plus, X, CheckCircle2, Zap } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import FileDropzone, { resolveFileUrl } from "@/components/FileDropzone";
import BuyModal from "@/components/BuyModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function CreateListingModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", image_url: "", btc_price: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/listings", {
        ...form,
        btc_price: parseFloat(form.btc_price),
      });
      onCreated(data);
      onClose();
      setForm({ title: "", description: "", image_url: "", btc_price: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="listing-form"
        className="bg-[#FDFBF7] border-2 border-black w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="font-display font-black uppercase text-2xl">List an item</div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <input
            required
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="listing-title"
            className="w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm"
          />
          <textarea
            placeholder="Description"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="listing-desc"
            className="w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm resize-none"
          />
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-2">Photo</div>
            <FileDropzone
              value={form.image_url}
              onChange={(u) => setForm({ ...form, image_url: u })}
              accept="image/*"
              label="Upload photo"
              testId="listing-image-upload"
              aspect="aspect-square max-h-[220px]"
            />
          </div>
          <input
            required
            type="number"
            step="0.00000001"
            min="0"
            placeholder="Price in BTC (e.g. 0.005)"
            value={form.btc_price}
            onChange={(e) => setForm({ ...form, btc_price: e.target.value })}
            data-testid="listing-price"
            className="w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="listing-submit"
          className="mt-6 w-full py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest btn-brutal disabled:opacity-50"
        >
          {loading ? "..." : "Publish listing"}
        </button>
      </form>
    </div>
  );
}

export default function Marketplace() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState({ fee_pct: 2.0 });
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/listings").then((r) => setItems(r.data));
    api.get("/marketplace/config").then((r) => setCfg(r.data));
  }, []);

  const markSold = async (item) => {
    if (!window.confirm(`Mark "${item.title}" as sold? A ${cfg.fee_pct}% platform fee will be recorded.`)) return;
    try {
      const { data } = await api.post(`/listings/${item.id}/mark-sold`, {});
      toast.success(`Sold · platform fee ${data.platform_fee_btc} BTC recorded`);
      setItems((its) =>
        its.map((x) => (x.id === item.id ? { ...x, sold: true, platform_fee_btc: data.platform_fee_btc } : x))
      );
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <h1 className="font-display font-black text-4xl lg:text-5xl uppercase tracking-tighter">
            Marketplace
          </h1>
          <p className="mt-2 text-sm text-[#0A0A0A]/60 font-mono uppercase">
            Priced in sats · {cfg.fee_pct}% platform fee on sold items
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="btn-new-listing"
          className="inline-flex items-center gap-2 px-4 py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest text-sm btn-brutal"
        >
          <Plus className="w-4 h-4" />
          List item
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border-2 border-black bg-white p-12 text-center font-mono text-sm text-[#0A0A0A]/60 uppercase tracking-widest">
          No listings yet. Be the first seller.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => {
            const isMine = it.seller_id === user?.id;
            return (
              <div
                key={it.id}
                data-testid={`listing-${it.id}`}
                className={`border-2 border-black bg-white card-brutal ${it.sold ? "opacity-70" : ""}`}
              >
                <div className="relative aspect-square bg-[#F7931A]/10 border-b-2 border-black overflow-hidden">
                  {it.image_url ? (
                    <img src={resolveFileUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-display font-black text-4xl text-[#F7931A]">
                      ₿
                    </div>
                  )}
                  {it.sold && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <div className="px-4 py-2 bg-[#F7931A] border-2 border-black font-display font-black uppercase">
                        Sold
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-display font-bold text-lg truncate">{it.title}</div>
                  <div className="text-xs text-[#0A0A0A]/60 font-mono uppercase mt-1">
                    by {it.seller?.name}
                  </div>
                </div>
                <div className="bg-[#F7931A] text-black font-mono px-4 py-2 text-sm font-bold border-t-2 border-black">
                  {it.btc_price} BTC
                </div>
                {isMine && !it.sold && (
                  <button
                    onClick={() => markSold(it)}
                    data-testid={`btn-mark-sold-${it.id}`}
                    className="w-full py-2 bg-black text-white border-t-2 border-black text-xs font-bold uppercase tracking-widest hover:bg-[#F7931A] hover:text-black transition-colors flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Mark sold
                  </button>
                )}
                {!isMine && !it.sold && (
                  <button
                    onClick={() => setBuying(it)}
                    data-testid={`btn-buy-${it.id}`}
                    className="w-full py-2 bg-black text-white border-t-2 border-black text-xs font-bold uppercase tracking-widest hover:bg-[#F7931A] hover:text-black transition-colors flex items-center justify-center gap-2"
                  >
                    <Zap className="w-3 h-3" />
                    Buy with Lightning
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CreateListingModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(item) => setItems((its) => [item, ...its])}
      />
      <BuyModal
        listing={buying}
        onClose={() => setBuying(null)}
      />
    </main>
  );
}
